import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { maxPrintsForTier } from '@/lib/featureKeys'
import { ScenePresetId, StudioMode, StudioRoomState } from '../../types'
import { CEIL_TONES, FLOOR_TONES, SCENE_OPTIONS, SCENE_PRESET_GEOMETRY, WALL_TONES } from '../../constants'
import { CLASSIC_FAMILIES, getClassicFamily, getClassicFamilyForId, getClassicGlyph, getClassicVariant, pickBestVariant } from '../../classicCatalog'
import { ClassicGlyph } from '@/design-system'
import { SegChips, SegPro, StudioSlider, Swatch } from '../shared'
import { FinishColorPanel } from './FinishColorPanel'
import { Head, isCustomTone, Kind, Note, ProPickerBadge, ProPickerBtn, Root, Scroll, Section, SectionLabel, Sub, Switch, SwatchGrid, ToggleRow, TitleInput } from './InspectorShared'

export interface RoomInspectorProps {
  title: string
  printCount: number
  room: StudioRoomState
  onRename: (name: string) => void
  onSetWallColor: (hex: string) => void
  onSetFloorColor: (hex: string) => void
  onSetCeilColor: (hex: string) => void
  onSetAmbience: (value: number) => void
  onSetFog: (value: number) => void
  onSetDwellSeconds: (value: number) => void
  onSetShowPins: (value: boolean) => void
  onSetShowPresenceDemo: (value: boolean) => void
  onSetUseHdri: (value: boolean) => void
  onSetScenePreset: (preset: ScenePresetId) => void
  onSetClassicTemplate: (templateId: number) => void
  classicLoading?: boolean
  classicError?: string | null
  /** Push undo snapshot after discrete edits / slider pointer-up. */
  onHistoryCommit?: () => void
}

const ModeTabs = styled(Box)({
  display: 'flex',
  gap: 4,
  padding: 3,
  borderRadius: 10,
  background: 'rgba(244,243,246,0.04)',
  border: '1px solid var(--ds-night-border)',
  marginBottom: 12,
})

const ModeTab = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  flex: 1,
  textAlign: 'center',
  height: 25,
  borderRadius: 7,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 650,
  color: $on ? 'var(--ds-night-ink)' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(244,243,246,0.09)' : 'transparent',
  transition: 'all 140ms ease',
}))

const ClassicNote = styled('div')({ fontSize: 10.5, lineHeight: 1.5, color: 'var(--ds-night-ink-subtle)', margin: '-2px 0 10px' })

const SectionLabelRow = styled(Box)({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 })

/**
 * Toggles `ClassicDetailNote` below — the SCENE section used to always show
 * a 4-sentence paragraph (styles count, orbit hint, walk-through status,
 * per-print framing). Only the first sentence is picker-relevant every time;
 * the rest is onboarding copy that a returning user doesn't need paid for
 * on every render, so it moves behind this `i` instead of being deleted.
 */
const InfoToggleBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 15,
  height: 15,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  fontWeight: 700,
  cursor: 'pointer',
  color: $on ? '#ffcf7d' : 'var(--ds-night-ink-subtle)',
  background: $on ? 'rgba(255,207,125,0.12)' : 'rgba(244,243,246,0.06)',
  border: `1px solid ${$on ? 'rgba(255,207,125,0.55)' : 'var(--ds-night-border)'}`,
  transition: 'color 140ms ease, border-color 140ms ease, background 140ms ease',
  '&:hover': { color: '#ffcf7d', borderColor: 'rgba(255,207,125,0.55)' },
}))

const ClassicDetailNote = styled('div')({
  fontSize: 10.5,
  lineHeight: 1.6,
  color: 'var(--ds-night-ink-subtle)',
  background: 'rgba(244,243,246,0.04)',
  border: '1px solid var(--ds-night-border)',
  borderRadius: 8,
  padding: '9px 10px',
  margin: '-2px 0 10px',
})

/**
 * Wall/Floor/Ceiling are room-level state (`room.wallColor` etc.) — true no
 * matter which of the 14 Classic families is active — so they don't belong
 * repeated on every card (that would waste space and imply they're
 * per-style, which they aren't). This strip surfaces the room's *current*
 * finish once, up top, and its dots track the live `room.wallColor` /
 * `floorColor` / `ceilColor` values — no separate state, no snapshot, just a
 * read of the same values the WALLS/FLOOR/CEILING swatches below write to.
 * `EDIT ↓` scrolls to that existing section rather than duplicating it.
 */
const FinishStrip = styled(Box)({
  display: 'flex',
  // `flex-start`, not `center` — if `FinishCaption` ever wraps to a 2nd
  // line (narrow column + a long enough string), centering the row would
  // shove `FinishEditLink` down into the middle of that 2-line block
  // instead of sitting level with the title on line 1.
  alignItems: 'flex-start',
  gap: 9,
  padding: '8px 9px',
  borderRadius: 9,
  background: 'rgba(244,243,246,0.03)',
  border: '1px solid var(--ds-night-border)',
  marginBottom: 10,
})

const FinishDots = styled(Box)({ display: 'flex', flexShrink: 0 })

const FinishDot = styled('span')({
  width: 15,
  height: 15,
  borderRadius: '50%',
  border: '1.5px solid rgba(6,5,9,0.5)',
  boxShadow: '0 0 0 1px rgba(244,243,246,0.08)',
  marginLeft: -5,
  '&:first-of-type': { marginLeft: 0 },
})

const FinishText = styled(Box)({ flex: 1, minWidth: 0 })
const FinishTitle = styled('div')({ fontSize: 10.5, fontWeight: 650, color: 'var(--ds-night-ink)' })
const FinishCaption = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  letterSpacing: '0.05em',
  color: 'var(--ds-night-ink-subtle)',
  marginTop: 1,
})

const FinishEditLink = styled('button')({
  all: 'unset',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.06em',
  color: '#ffcf7d',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
})

/** Cards beyond this stay collapsed until "Show all" — most people pick from a handful of favorites. */
const CLASSIC_COLLAPSE_COUNT = 6

const ClassicMoreBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  height: 30,
  margin: '2px 0 10px',
  borderRadius: 8,
  textAlign: 'center',
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(244,243,246,0.03)',
  color: 'var(--ds-night-ink-muted)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.08em',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, color 140ms ease',
  '&:hover': { borderColor: 'rgba(244,243,246,0.22)', color: 'var(--ds-night-ink)' },
})

const ClassicGrid = styled(Box)({ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 })

const ClassicCard = styled(Box, { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  boxSizing: 'border-box',
  width: 'calc(50% - 4px)',
  borderRadius: 10,
  border: `1px solid ${$on ? 'rgba(255,207,125,0.55)' : 'var(--ds-night-border)'}`,
  background: $on ? 'rgba(255,207,125,0.08)' : '#14131b',
  overflow: 'hidden',
  position: 'relative',
  transition: 'border-color 140ms ease, background 140ms ease',
  '&:hover': { borderColor: 'rgba(244,243,246,0.22)' },
}))

const ClassicCardMain = styled('button')({
  all: 'unset',
  display: 'block',
  boxSizing: 'border-box',
  width: '100%',
  cursor: 'pointer',
})

const ClassicThumb = styled(Box)({
  height: 52,
  background: '#1a1922',
  '& svg': { display: 'block', width: '100%', height: '100%' },
})

const ClassicTag = styled('span')({
  position: 'absolute',
  top: 5,
  right: 5,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 6,
  letterSpacing: '0.08em',
  color: '#ffcf7d',
  background: 'rgba(10,9,15,0.65)',
  borderRadius: 4,
  padding: '2px 4px',
  zIndex: 2,
})

const ClassicBody = styled(Box)({ padding: '7px 8px 4px' })
const ClassicName = styled('div', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  fontSize: 10.5,
  fontWeight: 650,
  color: $on ? '#ffcf7d' : 'var(--ds-night-ink)',
}))

/**
 * Drill-down print-count flow — "Variation 3" in
 * docs/mockups-v22/04-sidebar-scene-picker-remock.html (the one tagged
 * "recommended" and the one the interactive demo was built around). Tapping
 * a room card below auto-fits + applies it immediately (same instant-apply
 * as always — `onSetClassicTemplate` + `commit()`, no confirm gate) and
 * slides the panel to a dedicated count screen, so nailing the exact print
 * count gets its own moment instead of a tiny chip row under the room
 * choice. Re-entering an already-active room's screen never re-auto-fits —
 * it shows whatever count is already dialed in. `slideHeight` is measured
 * off whichever pane is current so the two screens (which have very
 * different natural heights) don't leave a gap or clip during the slide.
 */
const ClassicSlideWrap = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  transition: 'height 260ms cubic-bezier(0.4,0,0.2,1)',
})

const ClassicSlidePane = styled(Box, { shouldForwardProp: (p) => p !== '$pos' })<{ $pos: 'current' | 'left' | 'right' }>(
  ({ $pos }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transition: 'transform 260ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease',
    transform: $pos === 'current' ? 'translateX(0)' : $pos === 'left' ? 'translateX(-100%)' : 'translateX(100%)',
    opacity: $pos === 'current' ? 1 : 0,
    pointerEvents: $pos === 'current' ? 'auto' : 'none',
  }),
)

const BackLink = styled('button')({
  all: 'unset',
  display: 'block',
  cursor: 'pointer',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.06em',
  color: 'var(--ds-night-ink-subtle)',
  marginBottom: 10,
  transition: 'color 140ms ease',
  '&:hover': { color: 'var(--ds-night-ink)' },
})

const CountRoomLabel = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.08em',
  color: 'var(--ds-night-ink-subtle)',
  marginBottom: 8,
})

const BigGlyphBox = styled(Box)({
  position: 'relative',
  height: 108,
  borderRadius: 10,
  overflow: 'hidden',
  background: '#1a1922',
  border: '1px solid var(--ds-night-border)',
  marginBottom: 12,
  '& svg': { display: 'block', width: '100%', height: '100%' },
})

/** Overlay `<svg>` for the room glyph — same 200×100 stage as `ClassicGlyph`, positioned absolutely on top of it. */
const FrameOverlay = styled('svg')({ position: 'absolute', inset: 0, width: '100%', height: '100%' })

const BigStepperRow = styled(Box)({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 10 })

const BigStepBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  width: 30,
  height: 30,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(244,243,246,0.07)',
  border: '1px solid var(--ds-night-border)',
  color: 'var(--ds-night-ink)',
  cursor: 'pointer',
  fontSize: 14,
  transition: 'border-color 140ms ease, opacity 140ms ease',
  '&:hover': { borderColor: 'rgba(244,243,246,0.3)' },
  '&:disabled': { opacity: 0.3, cursor: 'default' },
})

const BigStepVal = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: 'var(--ds-night-ink)',
  minWidth: 84,
  textAlign: 'center',
})

const BigDots = styled(Box)({ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: 10 })
const BigDot = styled('span', { shouldForwardProp: (p) => p !== '$on' && p !== '$locked' })<{ $on?: boolean; $locked?: boolean }>(
  ({ $on, $locked }) => ({
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: $on ? '#ffcf7d' : $locked ? 'rgba(255,207,125,0.22)' : 'rgba(244,243,246,0.18)',
    boxShadow: $locked && !$on ? 'inset 0 0 0 1px rgba(255,207,125,0.35)' : 'none',
  }),
)

const LayoutCaption = styled('div')({
  textAlign: 'center',
  fontSize: 10.5,
  color: 'var(--ds-night-ink-subtle)',
  marginBottom: 14,
})

const ApplyBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  height: 34,
  marginBottom: 10,
  borderRadius: 8,
  textAlign: 'center',
  background: '#ffcf7d',
  color: '#0a0912',
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'opacity 140ms ease',
  '&:hover': { opacity: 0.9 },
})

/** Generic, geometry-agnostic blurb for the count screen — deliberately doesn't claim a specific layout we can't verify per family. */
function printCountCaption(count: number): string {
  if (count <= 1) return 'Single statement print, centered'
  if (count <= 3) return `${count} prints, evenly spaced`
  if (count <= 8) return `Even row of ${count}, evenly spaced`
  return `Dense salon cluster of ${count}`
}

/**
 * Schematic print-slot rects for the count screen's big glyph — the real
 * per-family layouts live in the actual `.babylon` meshes, not in any data
 * this component has, so this is a generic "N frames, evenly spaced, wraps
 * to a second row past 9" placeholder rather than a hand-authored table per
 * exact count (which would silently go stale the moment a family's real
 * variant counts changed). Coordinates are in `ClassicGlyph`'s 200×100
 * stage, frames sit inside its wall band (y 16–72).
 */
function layoutFrames(count: number): { x: number; y: number; w: number; h: number }[] {
  const n = Math.max(1, count)
  const rows = n > 9 ? 2 : 1
  const perRow = Math.ceil(n / rows)
  const gap = perRow <= 3 ? 10 : perRow <= 6 ? 6 : 3
  const rowGap = 6
  const wallTop = 16
  const wallHeight = 56
  const frameH = rows === 1 ? Math.min(38, wallHeight - 16) : Math.min(20, (wallHeight - 16 - rowGap) / 2)
  const totalRowsHeight = rows * frameH + (rows - 1) * rowGap
  const startY = wallTop + (wallHeight - totalRowsHeight) / 2
  const frameW = Math.min(40, (200 - gap * (perRow + 1)) / perRow)

  const rects: { x: number; y: number; w: number; h: number }[] = []
  let placed = 0
  for (let r = 0; r < rows; r++) {
    const countInRow = r === rows - 1 ? n - placed : perRow
    const rowWidth = countInRow * frameW + (countInRow - 1) * gap
    const rowStartX = (200 - rowWidth) / 2
    for (let c = 0; c < countInRow; c++) {
      rects.push({ x: rowStartX + c * (frameW + gap), y: startY + r * (frameH + rowGap), w: frameW, h: frameH })
      placed++
    }
  }
  return rects
}

/** Inspector shown when nothing (or the Room row) is selected. */
export function RoomInspector({
  title,
  printCount,
  room,
  onRename,
  onSetWallColor,
  onSetFloorColor,
  onSetCeilColor,
  onSetAmbience,
  onSetFog,
  onSetDwellSeconds,
  onSetShowPins,
  onSetShowPresenceDemo,
  onSetUseHdri: _onSetUseHdri,
  onSetScenePreset,
  onSetClassicTemplate,
  classicLoading,
  classicError,
  onHistoryCommit,
}: RoomInspectorProps) {
  const scenePreset = room.scenePreset || 'corridor'
  const mode: StudioMode = room.mode || 'studio'
  const activeFamily = mode === 'classic' && room.classicTemplateId ? getClassicFamilyForId(room.classicTemplateId) : undefined
  const commit = () => onHistoryCommit?.()
  const { isFeatureAvailable, showUpgrade, tier } = useFeatureGate()
  // Classic rooms/families themselves are free for every tier (unlike Studio's
  // Loft/Pavilion) — only the print *count* is plan-gated, mirroring the hard
  // enforcement at the hang chokepoint (`onHangWall` in Studio3DScreen, see
  // issues/docs/studio-print-limits-plan-gate-todo.md). Surfacing the cap here
  // too, at variant-pick time, avoids the silent mismatch where a free user
  // picks an 18-print room in this stepper but only 8 slots actually pad in.
  const printCap = maxPrintsForTier(tier)
  const [finishSurface, setFinishSurface] = useState<'wall' | 'floor' | 'ceiling' | null>(null)
  const [showSceneInfo, setShowSceneInfo] = useState(false)
  const hasCustomFinishes = isFeatureAvailable(FEATURE_KEYS.STUDIO_CUSTOM_FINISHES)
  // Classic's ceiling has no stock tone of its own to fall back to (these
  // loaded `.babylon` rooms predate a dedicated ceiling control) — mirrors
  // `useStudioLegacyRoom`'s own fallback to the wall tone.
  const ceilColor = room.ceilColor || (mode === 'classic' ? room.wallColor : SCENE_PRESET_GEOMETRY[scenePreset].ceilColor)
  const finishSectionRef = useRef<HTMLDivElement>(null)
  const hasCustomFinish =
    isCustomTone(room.wallColor, WALL_TONES) || isCustomTone(room.floorColor, FLOOR_TONES) || isCustomTone(ceilColor, CEIL_TONES)
  // Always starts collapsed. If the active family isn't in the default
  // slice (e.g. a gallery re-opened after picking one of the later rooms),
  // it's pinned in as one extra card so it's never hidden without the user
  // ever tapping "Show all" — `DEFAULT_LAUNCH_CLASSIC_FAMILY` (Classic 06)
  // itself already sits inside the default slice, so a brand-new gallery
  // never needs that extra card.
  const [showAllClassic, setShowAllClassic] = useState(false)
  const visibleClassicFamilies = showAllClassic
    ? CLASSIC_FAMILIES
    : activeFamily && !CLASSIC_FAMILIES.slice(0, CLASSIC_COLLAPSE_COUNT).some((f) => f.family === activeFamily.family)
      ? [...CLASSIC_FAMILIES.slice(0, CLASSIC_COLLAPSE_COUNT), activeFamily]
      : CLASSIC_FAMILIES.slice(0, CLASSIC_COLLAPSE_COUNT)

  // Drill-down count screen (Variation 3) — which room's count screen is
  // showing, independent of which room is actually applied to the room
  // (`activeFamily`/`room.classicTemplateId`), so browsing into a card never
  // itself changes anything until the room is tapped.
  const [classicScreen, setClassicScreen] = useState<'rooms' | 'count'>('rooms')
  const [draftFamilyNum, setDraftFamilyNum] = useState<number | null>(null)
  const draftFamily = draftFamilyNum != null ? getClassicFamily(draftFamilyNum) : undefined
  // Whatever count is already applied anywhere on the room — carried over
  // when auto-fitting a family that isn't active yet, so picking a
  // *different* room never changes the count on its own (only the stepper
  // does). Real photo count is only the fallback for a gallery that's never
  // had a Classic room chosen at all.
  const currentAppliedVariant = room.classicTemplateId ? getClassicVariant(room.classicTemplateId) : undefined
  const preferredPrintCount = currentAppliedVariant?.printCount ?? printCount
  const draftActiveVariant =
    draftFamily && room.classicTemplateId ? draftFamily.variants.find((v) => v.id === room.classicTemplateId) : undefined
  const draftDisplayVariant = draftFamily ? draftActiveVariant || pickBestVariant(draftFamily, preferredPrintCount) : undefined
  const draftVariantIdx =
    draftFamily && draftDisplayVariant ? draftFamily.variants.findIndex((v) => v.id === draftDisplayVariant.id) : -1
  const stepDraftTo = (nextIdx: number) => {
    if (!draftFamily) return
    const clamped = Math.max(0, Math.min(draftFamily.variants.length - 1, nextIdx))
    const v = draftFamily.variants[clamped]
    if (!v) return
    if (v.printCount > printCap) {
      showUpgrade({
        featureKey: FEATURE_KEYS.STUDIO_PRINT_QUOTA,
        headline: `${v.printCount} prints needs a bigger plan — this tier hangs up to ${printCap}`,
        upgradeTo: tier === 'pro' ? 'Studio' : 'Gallery',
      })
      return
    }
    onSetClassicTemplate(v.id)
    commit()
  }
  const roomsPaneRef = useRef<HTMLDivElement>(null)
  const countPaneRef = useRef<HTMLDivElement>(null)
  const [slideHeight, setSlideHeight] = useState<number>()
  useLayoutEffect(() => {
    const el = classicScreen === 'rooms' ? roomsPaneRef.current : countPaneRef.current
    if (el) setSlideHeight(el.offsetHeight)
  }, [classicScreen, visibleClassicFamilies.length, showAllClassic, draftFamilyNum, draftVariantIdx])

  const openCustomFinish = (surface: 'wall' | 'floor' | 'ceiling') => {
    // Opening the sheet stays free for everyone — it's the PRO strip inside
    // that sells the upgrade. The hard gate is on Apply (below), so a free
    // user can preview the extended palette but can't actually commit a
    // custom color to the room.
    setFinishSurface((cur) => (cur === surface ? null : surface))
  }

  const applyCustomFinish = (hex: string) => {
    if (!hasCustomFinishes) {
      showUpgrade({ featureKey: FEATURE_KEYS.STUDIO_CUSTOM_FINISHES, headline: 'Custom finishes are a Gallery+ perk' })
      return
    }
    if (finishSurface === 'wall') onSetWallColor(hex)
    else if (finishSurface === 'floor') onSetFloorColor(hex)
    else onSetCeilColor(hex)
    commit()
  }

  const sceneLabel =
    mode === 'classic' && activeFamily
      ? `${activeFamily.label.toUpperCase()} (CLASSIC)`
      : `${SCENE_PRESET_GEOMETRY[scenePreset].label.toUpperCase()} SCENE`

  return (
    <Root>
      <Head>
        <Kind>ROOM</Kind>
        <TitleInput value={title} spellCheck={false} onChange={(e) => onRename(e.target.value)} />
        <Sub>{sceneLabel} · {printCount} PRINTS</Sub>
      </Head>
      <Scroll>
        <Section>
          <SectionLabelRow>
            <SectionLabel sx={{ marginBottom: 0 }}>SCENE</SectionLabel>
            {mode !== 'studio' && !classicLoading && !classicError && (
              <InfoToggleBtn
                type='button'
                $on={showSceneInfo}
                aria-label='How the scene picker works'
                title='How this works'
                onClick={() => setShowSceneInfo((v) => !v)}
              >
                i
              </InfoToggleBtn>
            )}
          </SectionLabelRow>
          {/* Task #17: Classic is the launch default and Studio's mode tab
              is hidden going forward — corridor/loft/pavilion return in
              phase 2. Galleries already saved in Studio mode (built before
              this change, or any earlier test galleries) keep their tabs so
              they're never stranded mid-edit; only those rooms can still
              see/use this switcher at all. */}
          {mode === 'studio' && (
            <ModeTabs role='tablist'>
              <ModeTab
                type='button'
                $on={mode === 'studio'}
                onClick={() => {
                  onSetScenePreset(scenePreset)
                  commit()
                }}
              >
                Studio
              </ModeTab>
              <ModeTab
                type='button'
                $on={false}
                onClick={() => {
                  const fallback = activeFamily || CLASSIC_FAMILIES[0]
                  if (fallback) {
                    onSetClassicTemplate(pickBestVariant(fallback, printCount).id)
                    commit()
                  }
                }}
              >
                Classic
              </ModeTab>
            </ModeTabs>
          )}

          {mode === 'studio' ? (
            <SegChips
              options={SCENE_OPTIONS}
              value={scenePreset}
              onChange={(v) => {
                const opt = SCENE_OPTIONS.find((o) => o.value === v)
                if (opt?.pro && !isFeatureAvailable(FEATURE_KEYS.STUDIO_PREMIUM_SCENES)) {
                  showUpgrade({
                    featureKey: FEATURE_KEYS.STUDIO_PREMIUM_SCENES,
                    headline: `${opt.label} is a Gallery+ scene`,
                  })
                  return
                }
                onSetScenePreset(v as ScenePresetId)
                commit()
              }}
            />
          ) : (
            <>
              <ClassicNote>
                {classicLoading
                  ? 'Loading room…'
                  : classicError
                    ? `Couldn't load this room — ${classicError}`
                    : '14 styles below — tap a room to auto-fit, or pick an exact slot count.'}
              </ClassicNote>
              {showSceneInfo && !classicLoading && !classicError && (
                <ClassicDetailNote>
                  Orbit around a room to explore it before choosing — a full
                  walk-through is coming soon. Once you're in, select any
                  print to set its frame.
                </ClassicDetailNote>
              )}
              {!classicLoading && !classicError && (
                <FinishStrip>
                  <FinishDots>
                    <FinishDot style={{ background: room.wallColor }} />
                    <FinishDot style={{ background: room.floorColor }} />
                    <FinishDot style={{ background: ceilColor }} />
                  </FinishDots>
                  <FinishText>
                    <FinishTitle>{hasCustomFinish ? 'Custom finish' : 'Room finish'}</FinishTitle>
                    <FinishCaption>WALL · FLOOR · CEILING</FinishCaption>
                  </FinishText>
                  <FinishEditLink
                    type='button'
                    onClick={() => finishSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    EDIT ↓
                  </FinishEditLink>
                </FinishStrip>
              )}
              <ClassicSlideWrap style={{ height: slideHeight }}>
                <ClassicSlidePane ref={roomsPaneRef} $pos={classicScreen === 'rooms' ? 'current' : 'left'}>
                  <ClassicGrid>
                    {visibleClassicFamilies.map((family) => {
                      const isFamilyOn = activeFamily?.family === family.family
                      return (
                        <ClassicCard key={family.family} $on={isFamilyOn}>
                          <ClassicCardMain
                            type='button'
                            onClick={() => {
                              // Re-entering an already-active room's count
                              // screen must never re-auto-fit — that would
                              // silently discard a count the user already
                              // dialed in on a previous visit. Switching to a
                              // *different* room fits to `preferredPrintCount`
                              // (whatever's already applied), not the raw
                              // photo count — so the count only ever changes
                              // via the stepper, never just from picking a
                              // different room.
                              if (!isFamilyOn) {
                                onSetClassicTemplate(pickBestVariant(family, preferredPrintCount).id)
                                commit()
                              }
                              setDraftFamilyNum(family.family)
                              setClassicScreen('count')
                            }}
                          >
                            <ClassicTag>FIXED</ClassicTag>
                            <ClassicThumb>
                              <ClassicGlyph {...getClassicGlyph(family.family)} />
                            </ClassicThumb>
                            <ClassicBody>
                              <ClassicName $on={isFamilyOn}>{family.label}</ClassicName>
                            </ClassicBody>
                          </ClassicCardMain>
                        </ClassicCard>
                      )
                    })}
                  </ClassicGrid>
                  {CLASSIC_FAMILIES.length > CLASSIC_COLLAPSE_COUNT && (
                    <ClassicMoreBtn type='button' onClick={() => setShowAllClassic((v) => !v)}>
                      {showAllClassic ? 'SHOW FEWER ▴' : `SHOW ALL ${CLASSIC_FAMILIES.length} STYLES ▾`}
                    </ClassicMoreBtn>
                  )}
                </ClassicSlidePane>
                <ClassicSlidePane ref={countPaneRef} $pos={classicScreen === 'count' ? 'current' : 'right'}>
                  {draftFamily && draftDisplayVariant && (
                    <>
                      <BackLink type='button' onClick={() => setClassicScreen('rooms')}>
                        ← Rooms
                      </BackLink>
                      <CountRoomLabel>{draftFamily.label.toUpperCase()} — PRINT COUNT</CountRoomLabel>
                      <BigGlyphBox>
                        <ClassicGlyph {...getClassicGlyph(draftFamily.family)} />
                        <FrameOverlay viewBox='0 0 200 100' preserveAspectRatio='none'>
                          {layoutFrames(draftDisplayVariant.printCount).map((r, i) => (
                            <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} fill='#fff' stroke='rgba(0,0,0,0.18)' strokeWidth={1} />
                          ))}
                        </FrameOverlay>
                      </BigGlyphBox>
                      <BigStepperRow>
                        <BigStepBtn
                          type='button'
                          aria-label={`Fewer prints in ${draftFamily.label}`}
                          disabled={draftVariantIdx <= 0}
                          onClick={() => stepDraftTo(draftVariantIdx - 1)}
                        >
                          ◂
                        </BigStepBtn>
                        <BigStepVal>
                          {draftDisplayVariant.printCount} PRINT{draftDisplayVariant.printCount === 1 ? '' : 'S'}
                          {draftDisplayVariant.printCount > printCap && <SegPro>PRO</SegPro>}
                        </BigStepVal>
                        <BigStepBtn
                          type='button'
                          aria-label={`More prints in ${draftFamily.label}`}
                          disabled={draftVariantIdx >= draftFamily.variants.length - 1}
                          onClick={() => stepDraftTo(draftVariantIdx + 1)}
                        >
                          ▸
                        </BigStepBtn>
                      </BigStepperRow>
                      <BigDots>
                        {draftFamily.variants.map((v, i) => (
                          <BigDot key={v.id} $on={i === draftVariantIdx} $locked={v.printCount > printCap} />
                        ))}
                      </BigDots>
                      <LayoutCaption>
                        {printCountCaption(draftDisplayVariant.printCount)}
                        {draftDisplayVariant.printCount > printCap && (
                          <>
                            {' — '}
                            <span style={{ color: '#ffcf7d' }}>upgrade to hang past {printCap}</span>
                          </>
                        )}
                      </LayoutCaption>
                      <ApplyBtn type='button' onClick={() => setClassicScreen('rooms')}>
                        Apply to room
                      </ApplyBtn>
                    </>
                  )}
                </ClassicSlidePane>
              </ClassicSlideWrap>
            </>
          )}
        </Section>

        <Section ref={finishSectionRef}>
          <SectionLabel>WALLS</SectionLabel>
          <SwatchGrid>
            {WALL_TONES.map((hex) => (
              <Swatch
                key={hex}
                color={hex}
                size={24}
                selected={room.wallColor === hex}
                onClick={() => {
                  onSetWallColor(hex)
                  commit()
                }}
              />
            ))}
            <ProPickerBtn
              type='button'
              data-finish-trigger='wall'
              aria-label='Custom wall color'
              title='Custom color · Gallery+'
              $selected={isCustomTone(room.wallColor, WALL_TONES) || finishSurface === 'wall'}
              onClick={() => openCustomFinish('wall')}
            >
              <ProPickerBadge>PRO</ProPickerBadge>
            </ProPickerBtn>
          </SwatchGrid>
          <SectionLabel>FLOOR</SectionLabel>
          <SwatchGrid>
            {FLOOR_TONES.map((hex) => (
              <Swatch
                key={hex}
                color={hex}
                size={24}
                selected={room.floorColor === hex}
                onClick={() => {
                  onSetFloorColor(hex)
                  commit()
                }}
              />
            ))}
            <ProPickerBtn
              type='button'
              data-finish-trigger='floor'
              aria-label='Custom floor color'
              title='Custom color · Gallery+'
              $selected={isCustomTone(room.floorColor, FLOOR_TONES) || finishSurface === 'floor'}
              onClick={() => openCustomFinish('floor')}
            >
              <ProPickerBadge>PRO</ProPickerBadge>
            </ProPickerBtn>
          </SwatchGrid>
          <SectionLabel>CEILING</SectionLabel>
          <SwatchGrid>
            {CEIL_TONES.map((hex) => (
              <Swatch
                key={hex}
                color={hex}
                size={24}
                selected={ceilColor === hex}
                onClick={() => {
                  onSetCeilColor(hex)
                  commit()
                }}
              />
            ))}
            <ProPickerBtn
              type='button'
              data-finish-trigger='ceiling'
              aria-label='Custom ceiling color'
              title='Custom color · Gallery+'
              $selected={isCustomTone(ceilColor, CEIL_TONES) || finishSurface === 'ceiling'}
              onClick={() => openCustomFinish('ceiling')}
            >
              <ProPickerBadge>PRO</ProPickerBadge>
            </ProPickerBtn>
          </SwatchGrid>
        </Section>

        <Section>
          <SectionLabel>ATMOSPHERE</SectionLabel>
          <StudioSlider
            label='Ambience'
            value={room.ambience}
            min={0.2}
            max={1.3}
            step={0.01}
            onChange={onSetAmbience}
            onCommit={commit}
            valueFormatter={(v) => v.toFixed(2)}
          />
          <StudioSlider
            label='Haze'
            value={room.fog}
            min={0}
            max={0.03}
            step={0.001}
            onChange={onSetFog}
            onCommit={commit}
            valueFormatter={(v) => v.toFixed(3)}
          />
          {/* TODO: re-enable when IBL/PBR pass is ready
          <ToggleRow>
            <span>Natural IBL</span>
            <Switch $on={!!room.useHdri} onClick={() => onSetUseHdri(!room.useHdri)} />
          </ToggleRow>
          <Note>Optional indoor environment light — soft floor sheen before full PBR.</Note>
          */}
        </Section>

        <Section>
          <SectionLabel>GUIDED TOUR</SectionLabel>
          <StudioSlider
            label='Dwell'
            value={room.dwellSeconds}
            min={1}
            max={8}
            step={0.5}
            onChange={onSetDwellSeconds}
            onCommit={commit}
            valueFormatter={(v) => `${v.toFixed(1)} S`}
          />
          <Note>Order and pacing of the walkthrough guests get when they press play. Edit stops in the Tour tab.</Note>
        </Section>

        <Section>
          <SectionLabel>STORY LAYER</SectionLabel>
          <ToggleRow>
            <span>Pinned notes in room</span>
            <Switch
              $on={room.showPins}
              onClick={() => {
                onSetShowPins(!room.showPins)
                commit()
              }}
            />
          </ToggleRow>
          <Note>Pinned notes hang beside prints in the room. Guests with the key can see them.</Note>
          {/*
            "Presence cursor (demo)" toggle used to live here — a fake guest
            cursor for showcasing what realtime presence could look like.
            Real presence (StudioTopBar's viewer avatars, driven by the live
            topic_view socket event — see useTopicViewers) shipped instead,
            so the demo cursor is retired. The underlying PresenceCursor
            component and showPresenceDemo state are left in place in case a
            true walkable multiplayer cursor becomes a real feature later.
          */}
        </Section>
      </Scroll>

      {finishSurface && (
        <FinishColorPanel
          surface={finishSurface}
          initialColor={finishSurface === 'wall' ? room.wallColor : finishSurface === 'floor' ? room.floorColor : ceilColor}
          showProStrip={!hasCustomFinishes}
          onApply={applyCustomFinish}
          onClose={() => setFinishSurface(null)}
          onUpgrade={() => {
            showUpgrade({
              featureKey: FEATURE_KEYS.STUDIO_CUSTOM_FINISHES,
              headline: 'Custom finishes are a Gallery+ perk',
            })
          }}
        />
      )}
    </Root>
  )
}
